import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import GridShape from "../../components/common/GridShape";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import { activateUser } from "../../services/authService";
import PageMeta from "../../components/common/PageMeta";
import { buildAppUrl } from "../../config/app";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFallback, localizeApiMessage } from "../../utils/apiMessageI18n";
import RegisterLoader from "../../components/common/RegisterLoader";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import { useAuthUser } from "../../context/AuthUserContext";

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
const UPPERCASE_RULE = /[A-Z]/;
const LOWERCASE_RULE = /[a-z]/;
const NUMBER_RULE = /\d/;
const SPECIAL_RULE = /[^A-Za-z\d]/;

function RequirementIndicator({ valid }: { valid: boolean }) {
  if (!valid) {
    return <span>O</span>;
  }

  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M10 3L4.8 8.2L2 5.4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ActivateAccount() {
  const { language } = useLanguage();
  const { refreshUser } = useAuthUser();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useMemo(
    () => new URLSearchParams(location.search).get("token")?.trim() || "",
    [location.search]
  );
  const activateUrlExample = useMemo(
    () => buildAppUrl("/activate?token=SEU_TOKEN"),
    []
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const hasMinLength = password.length >= 8;
  const hasUppercase = UPPERCASE_RULE.test(password);
  const hasLowercase = LOWERCASE_RULE.test(password);
  const hasNumber = NUMBER_RULE.test(password);
  const hasSpecial = SPECIAL_RULE.test(password);
  const isPasswordValid = PASSWORD_RULE.test(password);
  const isConfirmValid = confirmPassword.length > 0 && password === confirmPassword;
  const canSubmit = token.length > 0 && isPasswordValid && isConfirmValid && !isSubmitting;
  const i18n = {
    title: getLocalizedFallback(language, {
      pt: "Definir senha",
      es: "Definir contrasena",
      en: "Set Password",
    }),
    subtitle: getLocalizedFallback(language, {
      pt: "Crie sua senha para acessar o sistema.",
      es: "Crea tu contrasena para acceder al sistema.",
      en: "Create your password to access the system.",
    }),
    passwordLabel: getLocalizedFallback(language, {
      pt: "Nova senha",
      es: "Nueva contrasena",
      en: "New Password",
    }),
    passwordPlaceholder: getLocalizedFallback(language, {
      pt: "Digite sua nova senha",
      es: "Ingresa tu nueva contrasena",
      en: "Enter your new password",
    }),
    confirmLabel: getLocalizedFallback(language, {
      pt: "Confirmar senha",
      es: "Confirmar contrasena",
      en: "Confirm Password",
    }),
    confirmPlaceholder: getLocalizedFallback(language, {
      pt: "Repita sua nova senha",
      es: "Repite tu nueva contrasena",
      en: "Repeat your new password",
    }),
    reqMinLength: getLocalizedFallback(language, {
      pt: "Minimo 8 caracteres",
      es: "Minimo 8 caracteres",
      en: "Minimum 8 characters",
    }),
    reqUppercase: getLocalizedFallback(language, {
      pt: "Pelo menos 1 letra maiuscula",
      es: "Al menos 1 letra mayuscula",
      en: "At least 1 uppercase letter",
    }),
    reqLowercase: getLocalizedFallback(language, {
      pt: "Pelo menos 1 letra minuscula",
      es: "Al menos 1 letra minuscula",
      en: "At least 1 lowercase letter",
    }),
    reqNumber: getLocalizedFallback(language, {
      pt: "Pelo menos 1 numero",
      es: "Al menos 1 numero",
      en: "At least 1 number",
    }),
    reqSpecial: getLocalizedFallback(language, {
      pt: "Pelo menos 1 caractere especial (!@#$%&*)",
      es: "Al menos 1 caracter especial (!@#$%&*)",
      en: "At least 1 special character (!@#$%&*)",
    }),
    tokenNotFound: getLocalizedFallback(language, {
      pt: "Token de ativacao nao encontrado na URL. Formato esperado:",
      es: "Token de activacion no encontrado en la URL. Formato esperado:",
      en: "Activation token not found in URL. Expected format:",
    }),
    activateButton: getLocalizedFallback(language, {
      pt: "Definir senha",
      es: "Definir contrasena",
      en: "Set Password",
    }),
    activatingButton: getLocalizedFallback(language, {
      pt: "Processando...",
      es: "Procesando...",
      en: "Processing...",
    }),
    backTo: getLocalizedFallback(language, {
      pt: "Voltar para",
      es: "Volver a",
      en: "Back to",
    }),
    signIn: getLocalizedFallback(language, {
      pt: "Entrar",
      es: "Iniciar sesion",
      en: "Sign In",
    }),
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await activateUser({
        token,
        new_password: password,
      });

      const accessToken = response.data?.access_token;
      if (accessToken) {
        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("token_type", response.data?.token_type || "Bearer");
        localStorage.setItem(
          "expires_in",
          String(response.data?.expires_in ?? "")
        );
        await refreshUser();
        navigate("/");
        return;
      }

      const fallbackMessage = getLocalizedFallback(language, {
        pt: "Nao foi possivel concluir a ativacao da conta.",
        es: "No fue posible completar la activacion de la cuenta.",
        en: "Could not complete account activation.",
      });
      setErrorMessage(localizeApiMessage(response.data?.message || "", language) || fallbackMessage);
    } catch (error) {
      const fallbackMessage = getLocalizedFallback(language, {
        pt: "Nao foi possivel ativar a conta. Verifique o token e tente novamente.",
        es: "No se pudo activar la cuenta. Verifica el token e intenta nuevamente.",
        en: "Could not activate account. Check the token and try again.",
      });
      const rawMessage = error instanceof Error ? error.message : "";
      setErrorMessage(localizeApiMessage(rawMessage, language) || fallbackMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Activate Account | Gerify"
        description="Tela de ativacao de conta com definicao de senha."
      />
      {isSubmitting && <RegisterLoader />}
      <div className="relative flex w-full h-screen overflow-hidden bg-white z-1 dark:bg-gray-900">
        <div className="flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8">
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            <div className="mb-5 sm:mb-8">
              <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                {i18n.title}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {i18n.subtitle}
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div>
                  <Label>
                    {i18n.passwordLabel}
                    <span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={i18n.passwordPlaceholder}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={password.length > 0 && !isPasswordValid}
                      className="pr-11"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                      )}
                    </span>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    <li className={`flex items-center gap-2 text-xs ${hasMinLength ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                      <span className={`${hasMinLength ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                        <RequirementIndicator valid={hasMinLength} />
                      </span>
                      <span>{i18n.reqMinLength}</span>
                    </li>
                    <li className={`flex items-center gap-2 text-xs ${hasUppercase ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                      <span className={`${hasUppercase ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                        <RequirementIndicator valid={hasUppercase} />
                      </span>
                      <span>{i18n.reqUppercase}</span>
                    </li>
                    <li className={`flex items-center gap-2 text-xs ${hasLowercase ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                      <span className={`${hasLowercase ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                        <RequirementIndicator valid={hasLowercase} />
                      </span>
                      <span>{i18n.reqLowercase}</span>
                    </li>
                    <li className={`flex items-center gap-2 text-xs ${hasNumber ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                      <span className={`${hasNumber ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                        <RequirementIndicator valid={hasNumber} />
                      </span>
                      <span>{i18n.reqNumber}</span>
                    </li>
                    <li className={`flex items-center gap-2 text-xs ${hasSpecial ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                      <span className={`${hasSpecial ? "text-success-600" : "text-gray-700 dark:text-gray-300"}`}>
                        <RequirementIndicator valid={hasSpecial} />
                      </span>
                      <span>{i18n.reqSpecial}</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <Label>
                    {i18n.confirmLabel}
                    <span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={i18n.confirmPlaceholder}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      error={confirmPassword.length > 0 && !isConfirmValid}
                      className="pr-11"
                    />
                    <span
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                      aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showConfirmPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                      )}
                    </span>
                  </div>
                </div>

                {!token && (
                  <p className="text-sm text-error-500" role="alert">
                    {i18n.tokenNotFound} {activateUrlExample}
                  </p>
                )}
                {errorMessage && (
                  <p className="text-sm text-error-500" role="alert">
                    {errorMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition shadow-theme-xs ${
                    canSubmit ? "bg-brand-500 hover:bg-brand-600" : "cursor-not-allowed bg-gray-400"
                  }`}
                >
                  {isSubmitting ? i18n.activatingButton : i18n.activateButton}
                </button>
              </div>
            </form>
            <p className="mt-5 text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
              {i18n.backTo}{" "}
              <Link to="/signin" className="text-brand-500 hover:text-brand-600 dark:text-brand-400">
                {i18n.signIn}
              </Link>
            </p>
          </div>
        </div>
        <div className="relative items-center justify-center flex-1 hidden p-8 z-1 bg-brand-950 dark:bg-white/5 lg:flex">
          <GridShape />
          <div className="flex flex-col items-center max-w-lg">
            <div className="block mb-4">
              <img src="./images/logo/auth-logo.svg" alt="Logo" className="w-[520px] max-w-full" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
